import { useMemo, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { useTranslation } from 'react-i18next';
import {
  resolveFormattingLocale,
  type ReportQuery,
  type WorkspaceFormats,
} from '@eis/core/reporting';
import { EmptyState } from '@eis/patterns';
import {
  PivotGrid,
  ReportChart,
  buildPivot,
  displayFieldName,
  type ResultColumn,
} from '@eis/patterns/reporting';
import type { ReportDocument } from '../../../lib/document';
import {
  cellFormatter,
  hasResultData,
  resultColumnsOf,
  resultRowsOf,
  titleOfColumn,
} from '../../../lib/result';

export type ResultMode = 'auto' | 'table' | 'pivot' | 'chart';

export interface ReportResultProps {
  readonly doc: ReportDocument;
  readonly query: ReportQuery | undefined;
  readonly settings?: WorkspaceFormats;
  /** The editor's preview segment (Tabelle | Pivot | Diagramm); the viewer draws the report's type. */
  readonly mode?: ResultMode;
  /** A table's further pages (Q-3): already loaded rows beyond the first page. */
  readonly moreRows?: readonly (string | null)[][];
  readonly onLoadMore?: () => void;
  readonly hasMore?: boolean;
  /** The viewer's "Daten anzeigen" under a chart (C-3). */
  readonly showData?: boolean;
  /** The sort in force (the viewer's, else the report's): the table header shows it. */
  readonly sort?: readonly { readonly by: string; readonly direction: 'ASC' | 'DESC' }[];
  /** A table column's header sorts by it (owner feedback 2026-11-01); absent: headers are text. */
  readonly onSort?: (by: string) => void;
}

/**
 * What a finished query looks like (SDD-024 P-3, C-1, §7 step 7): a TABLE's rows, a pivot grid, or a
 * chart with its pivot one click away — one renderer for both providers, every cell formatted by the
 * report's formats over the workspace defaults. No rows is the empty state of board 10.
 */
export function ReportResult({
  doc,
  query,
  settings,
  mode = 'auto',
  moreRows = [],
  onLoadMore,
  hasMore = false,
  showData = false,
  sort = [],
  onSort,
}: ReportResultProps): ReactNode {
  const { t, i18n } = useTranslation('reporting');
  const locale = resolveFormattingLocale(i18n.language);
  const columns = useMemo(() => resultColumnsOf(query), [query]);
  const rows = useMemo(() => [...resultRowsOf(query), ...moreRows], [query, moreRows]);
  const format = useMemo(
    () => cellFormatter(doc, columns, settings, locale),
    [doc, columns, settings, locale],
  );
  const titleOf = (column: ResultColumn): string =>
    titleOfColumn(doc, column) ?? displayFieldName(column.label);

  if (query?.state !== 'SUCCEEDED') return null;
  if (!hasResultData(query, moreRows)) {
    return <EmptyState compact title={t('viewer.noData')} description={t('viewer.noDataHint')} />;
  }

  const kind: ResultMode =
    mode !== 'auto'
      ? mode
      : doc.reportType === 'TABLE'
        ? 'table'
        : doc.reportType === 'CHART'
          ? 'chart'
          : 'pivot';
  const isPivotResult = columns.some((c) => c.role === 'GROUPING');

  if (kind === 'table' || !isPivotResult) {
    const shown = columns.filter((c) => c.role !== 'GROUPING' && c.role !== 'CURRENCY');
    return (
      <Box data-testid="report-table">
        <TableContainer sx={{ maxHeight: 640 }} tabIndex={0}>
          <Table size="small" stickyHeader aria-label={doc.name}>
            <TableHead>
              <TableRow>
                {shown.map((c) => {
                  // A table's column is sorted by its field (the result column's key, SQL-1).
                  const by =
                    doc.table?.columns.some((col) => col.field === c.key) === true
                      ? c.key
                      : undefined;
                  const active = by === undefined ? undefined : sort.find((s) => s.by === by);
                  return (
                    <TableCell
                      key={c.key}
                      align={c.type === 'NUMBER' ? 'right' : 'left'}
                      sx={{ fontWeight: 600 }}
                      {...(active === undefined
                        ? {}
                        : { 'aria-sort': active.direction === 'ASC' ? 'ascending' : 'descending' })}
                    >
                      {onSort !== undefined && by !== undefined ? (
                        <TableSortLabel
                          active={active !== undefined}
                          direction={active?.direction === 'DESC' ? 'desc' : 'asc'}
                          onClick={() => {
                            onSort(by);
                          }}
                        >
                          {titleOf(c)}
                        </TableSortLabel>
                      ) : (
                        titleOf(c)
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, r) => (
                <TableRow
                  key={`${String(r)}:${row[0] ?? ''}`}
                  data-row-key={
                    shown[0] === undefined ? '' : (row[columns.indexOf(shown[0])] ?? '')
                  }
                >
                  {shown.map((c) => {
                    const at = columns.indexOf(c);
                    return (
                      <TableCell
                        key={c.key}
                        align={c.type === 'NUMBER' ? 'right' : 'left'}
                        data-column={titleOf(c)}
                        sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {format(c, row[at] ?? null, row)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {query.truncated === true && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {t('viewer.truncated', { limit: doc.table?.limit ?? rows.length })}
          </Alert>
        )}
        {hasMore && onLoadMore !== undefined && (
          <Button size="small" onClick={onLoadMore} sx={{ mt: 1 }}>
            {t('viewer.loadMore')}
          </Button>
        )}
      </Box>
    );
  }

  const model = buildPivot(columns, rows);
  const grid = (
    <PivotGrid
      model={model}
      formatCell={(c, raw) => format(c, raw)}
      ariaLabel={doc.name}
      titleOf={titleOf}
    />
  );
  if (kind === 'pivot') return grid;
  return (
    <Box>
      <ReportChart
        model={model}
        chart={doc.chart ?? { type: 'BAR' }}
        title={doc.name}
        formatCell={(c, raw) => format(c, raw)}
      />
      {showData && <Box sx={{ mt: 2 }}>{grid}</Box>}
    </Box>
  );
}
